import { Component, computed, effect, inject, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Router } from '@angular/router';
import { EventTrackingService } from '@core/services/event-tracking.service';
import { EEventType } from '@enums/event-tracking.enum';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ICheckChannelResponse } from '@pages/creators/creators.interface';
import { CreatorsStore } from '@pages/creators/creators.store';
import { AddPlatformInputComponent } from '@shared/components/add-platform-input/add-platform-input.component';
import { AddPlatformInputStore } from '@shared/components/add-platform-input/add-platform-input.store';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { SideDialogComponent } from '@shared/components/side-dialog/side-dialog.component';
import { IAddPlatformDialogData } from './add-platform-dialog.interface';

@Component({
  imports: [
    SideDialogComponent,
    TranslateModule,
    MatFormFieldModule,
    MatInputModule,
    DotsSpinnerComponent,
    ReactiveFormsModule,
    MatButtonModule,
    AddPlatformInputComponent,
  ],
  providers: [CreatorsStore, AddPlatformInputStore],
  selector: 'app-add-platform-dialog',
  templateUrl: './add-platform-dialog.component.html',
})
export class AddPlatformDialogComponent {
  public readonly form = new FormGroup({
    url: new FormControl<string | null>(null),
  });

  public readonly createMode = signal<boolean>(false);
  public readonly creatingCreator = signal<boolean>(false);
  public readonly validatedChannelUrl = signal<string | null>(null);
  public readonly validatedChannelResponse = signal<ICheckChannelResponse | null>(null);

  public readonly title = computed(() => {
    return this.translate.instant(
      this.createMode()
        ? 'pages.creatorDetails.addPlatform.addCreatorTitle'
        : 'pages.creatorDetails.addPlatform.addPlatformTitle',
    );
  });

  public readonly data = inject<IAddPlatformDialogData>(MAT_DIALOG_DATA);
  public readonly addPlatformInputStore = inject(AddPlatformInputStore);
  private readonly creatorsStore = inject(CreatorsStore);
  private readonly dialogRef = inject(MatDialogRef<AddPlatformDialogComponent>);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly eventTrackingService = inject(EventTrackingService);

  constructor() {
    this.createMode.set(!this.data.creator);
    this.createNewCreatorEffect();
    this.createNewPlatformEffect();
    this.createNewCreatorErrorEffect();
    this.clearCreateErrorOnNewValidation();
  }

  public onSubmit(): void {
    if (!this.validatedChannelUrl() || !this.validatedChannelResponse()) {
      return;
    }

    this.creatingCreator.set(true);

    if (this.data.creator) {
      this.addPlatformInputStore.createChannel({
        channel: { url: this.validatedChannelUrl()! },
        creatorId: this.data.creator.id!,
        // creatorMode routes this to POST /channels/current (CREATOR_UPDATE_SELF) instead of
        // POST /channels?creator_id=... (CREATOR_UPDATE_OWN, staff-only) - see
        // IAddPlatformDialogData's creatorMode doc comment for why this matters.
        creatorMode: this.data.creatorMode,
      });
    } else {
      this.creatorsStore.createCreator({
        channels: [
          {
            url: this.validatedChannelUrl()!,
          },
        ],
        icon: this.validatedChannelResponse()?.iconUrl!,
        name: this.validatedChannelResponse()?.channelId!,
      });
    }
  }

  public onCloseClick(): void {
    this.dialogRef.close();
  }

  private createNewCreatorEffect(): void {
    effect(() => {
      const newCreator = this.creatorsStore.newCreator$();

      if (newCreator) {
        if (this.data.redirectToCreatorDetails) {
          this.eventTrackingService.track(EEventType.creatorCreated, { creatorId: newCreator.id });

          this.router.navigate(['/creators', newCreator.id]).then(() => {
            this.dialogRef.close();
            this.creatingCreator.set(false);
          });
        } else {
          // Campaign-creation wizard opens this dialog with redirectToCreatorDetails: false.
          // creator_created (#57) only fires for the standalone page (above); the wizard fires
          // campaign_creation_creator_created (#15) instead - campaignId auto-fills from
          // campaign-form.component.ts's campaign context, since this dialog's data carries no
          // campaign reference.
          this.eventTrackingService.track(EEventType.campaignCreationCreatorCreated, {
            creatorId: newCreator.id,
          });

          this.dialogRef.close(newCreator);
          this.creatingCreator.set(false);
        }
      }
    });
  }

  private createNewPlatformEffect(): void {
    effect(() => {
      const createdChannel = this.addPlatformInputStore.createdChannel$();

      if (createdChannel) {
        this.eventTrackingService.track(EEventType.creatorChannelAdded, { creatorId: this.data.creator?.id });
        this.dialogRef.close(createdChannel);
        this.creatingCreator.set(false);
      }
    });
  }

  private createNewCreatorErrorEffect(): void {
    effect(() => {
      if (this.creatorsStore.error$() || this.addPlatformInputStore.error$()) {
        this.creatingCreator.set(false);
        this.validatedChannelUrl.set(null);
        this.validatedChannelResponse.set(null);
      }
    });
  }

  private clearCreateErrorOnNewValidation(): void {
    effect(() => {
      if (this.validatedChannelUrl()) {
        this.addPlatformInputStore.patchState({ error: null, errorCreatorExists: false, errorCreatorId: null });
      }
    });
  }
}
